import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Eye, Save } from "lucide-react";
import AppHeader from "@/components/layout/AppHeader";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { IaReviewPanel, VersionesPanel, PreviewPanel } from "@/components/plantillas/PlantillaPanels";

function SaveDialog({ open, onOpenChange, onSave }) {
  const [nota, setNota] = useState("");
  const [validada, setValidada] = useState(false);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="plantilla-save-dialog">
        <DialogHeader><DialogTitle>Guardar versión nueva</DialogTitle></DialogHeader>
        <textarea value={nota} onChange={(e) => setNota(e.target.value)} rows={3} data-testid="plantilla-nota"
                  placeholder='Ej. "Corrección de redacción, con VALIDACIÓN LEGAL"'
                  className="w-full rounded-xl border border-[#CBD5E1] p-3 text-sm" />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={validada} onChange={(e) => setValidada(e.target.checked)} data-testid="plantilla-validada" />
          Ya tiene VALIDACIÓN LEGAL (se usa de inmediato en contratos nuevos)
        </label>
        <DialogFooter>
          <button onClick={() => onSave(nota, validada)} className="bm-btn-primary" data-testid="plantilla-save-confirm">Guardar</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminPlantillaEditorPage() {
  const { clave } = useParams();
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const [texto, setTexto] = useState("");
  const [errores, setErrores] = useState([]);
  const [bloques, setBloques] = useState(null);
  const [saveOpen, setSaveOpen] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/plantillas/${clave}`);
    setInfo(data);
    setTexto(data.ultima?.texto || "");
  }, [clave]);
  useEffect(() => { load(); }, [load]);

  const preview = async () => {
    const { data } = await api.post(`/admin/plantillas/${clave}/vista-previa`, { texto });
    setErrores(data.errores);
    setBloques(data.errores.length ? null : data.bloques);
    if (data.errores.length) toast.error("La plantilla tiene errores");
  };
  const save = async (nota, validada, origen = "manual", t = texto) => {
    try {
      await api.post(`/admin/plantillas/${clave}/versiones`, { texto: t, nota, validada, origen });
      toast.success("Versión guardada");
      setSaveOpen(false); setErrores([]);
      load();
    } catch (e) {
      const det = e.response?.data?.detail;
      if (det?.errores) { setErrores(det.errores); setSaveOpen(false); toast.error("No se guardó: revisa los errores"); }
      else toast.error(det || "No se pudo guardar");
    }
  };
  const ver = async (v) => {
    const { data } = await api.get(`/admin/plantillas/${clave}/versiones/${v.id}`);
    setTexto(data.texto);
    toast(`Mostrando v${v.version} en el editor`);
  };
  const restaurar = async (v) => { await api.post(`/admin/plantillas/${clave}/versiones/${v.id}/restaurar`); toast.success(`Restaurada v${v.version}`); load(); };
  const validar = async (v) => { await api.post(`/admin/plantillas/${clave}/versiones/${v.id}/validar`); toast.success("Marcada como validada"); load(); };

  if (user?.role !== "owner") return <div className="min-h-screen"><AppHeader /><p className="p-10 text-center">Solo el owner puede editar plantillas.</p></div>;
  if (!info) return <div className="min-h-screen"><AppHeader /><p className="p-10 text-center text-slate-500">Cargando…</p></div>;

  return (
    <div className="min-h-screen">
      <AppHeader />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-4" data-testid="plantilla-editor-page">
        <Link to="/app/admin/plantillas" className="text-sm text-slate-600 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Plantillas</Link>
        <h1 className="text-2xl sm:text-3xl font-heading font-extrabold text-slate-900">{info.tipo.nombre}</h1>
        <p className="text-xs text-slate-500">En uso: v{info.activa_version} · Marcadores: {"{{variable}}"}, {"{% si campo %}…{% fin %}"}, {"{{EL_ARRENDADOR}}"}, {"{{ORDINAL}}"}</p>
        <div className="grid lg:grid-cols-[1fr_340px] gap-4">
          <div className="space-y-3">
            <textarea value={texto} onChange={(e) => { setTexto(e.target.value); setBloques(null); }} spellCheck={false} data-testid="plantilla-textarea"
                      className="w-full h-[55vh] rounded-xl border border-[#CBD5E1] p-3 font-mono text-[12px] leading-5 bg-white" />
            {errores.length > 0 && (
              <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-800" data-testid="plantilla-errores">
                {errores.map((e) => <div key={e}>• {e}</div>)}
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <button onClick={preview} className="bm-btn-secondary" data-testid="plantilla-preview-btn"><Eye className="w-4 h-4" /> Vista previa</button>
              <button onClick={() => setSaveOpen(true)} className="bm-btn-primary" data-testid="plantilla-save-btn"><Save className="w-4 h-4" /> Guardar versión</button>
            </div>
            {bloques && <PreviewPanel bloques={bloques} />}
          </div>
          <div className="space-y-4">
            <IaReviewPanel clave={clave} texto={texto} onApply={(t) => { setTexto(t); save("", false, "ia", t); }} />
            <VersionesPanel versiones={info.versiones} activa={info.activa_version} onVer={ver} onRestaurar={restaurar} onValidar={validar} />
            <div className="bm-card p-4 text-xs text-slate-600" data-testid="plantilla-variables">
              <div className="font-semibold text-slate-900 mb-1">Variables disponibles</div>
              <div className="break-words">{info.variables.campos.join(", ")}</div>
              <div className="mt-2 break-words">Personas: {info.variables.personas.join(", ")} (ej. {"{{propietario.nombre}}"})</div>
              <div className="mt-2 break-words">Concordancia: {info.variables.terminos.map((t) => `EL_${t}`).join(", ")}</div>
            </div>
          </div>
        </div>
        <SaveDialog open={saveOpen} onOpenChange={setSaveOpen} onSave={(n, v) => save(n, v)} />
      </main>
    </div>
  );
}
